import type { MapPoint } from '@map-ui';
import type { ProductListItem } from '@travel-planner/shared/schemas';
import { resolveThumbnailUrl } from '@travel-planner/shared/utils';

export type ProductMarkerVm = {
  id: string;
  point: MapPoint;
  title: string;
  thumbnail: string | null;
  category: string | null;
  subcategory: string | null;
};

// `productListItem.locationPoint` is either a `{ lat, lng }` object or a
// tuple (or null). The list query returns the raw Drizzle geometry, whose
// tuple is PostGIS order **[lng, lat]** (longitude first) — NOT [lat, lng].
// Reading it as [lat, lng] swaps the axes (Iberia → East Africa). Normalise
// both forms to a `MapPoint`, dropping non-finite coords.
export const normalizeProductPoint = (
  locationPoint: ProductListItem['locationPoint']
): MapPoint | null => {
  if (!locationPoint) return null;
  const lat = Array.isArray(locationPoint)
    ? locationPoint[1]
    : locationPoint.lat;
  const lng = Array.isArray(locationPoint)
    ? locationPoint[0]
    : locationPoint.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

// First non-empty localized title in `preferLanguage`, else any entry.
// Mirrors `resolveLocalizedText` used by the picker cards, so a marker
// tooltip never disagrees with the card it belongs to: hosts that know the
// proposal language (the planner) pass it; the rest keep the English-first
// default.
export const readProductTitle = (
  raw: unknown,
  preferLanguage?: string | null
): string => {
  if (!Array.isArray(raw)) return 'Untitled product';
  const isText = (
    entry: unknown
  ): entry is { languageCode: string; text: string } =>
    typeof entry === 'object' &&
    entry !== null &&
    'text' in entry &&
    typeof (entry as { text: unknown }).text === 'string' &&
    (entry as { text: string }).text.trim().length > 0;
  const wanted = preferLanguage || 'en';
  const preferred = raw.find(
    (entry): entry is { languageCode: string; text: string } =>
      isText(entry) &&
      (entry as { languageCode?: string }).languageCode === wanted
  );
  if (preferred) return preferred.text;
  const first = raw.find(isText);
  return first ? first.text : 'Untitled product';
};

// Builds the marker view-models for the map: one per geo-located product,
// skipping rows without a usable coordinate. Pure — the component memoises
// the result and renders it.
export const buildProductMarkers = (
  products: ReadonlyArray<ProductListItem>,
  preferLanguage?: string | null
): ProductMarkerVm[] =>
  products.flatMap((product) => {
    const point = normalizeProductPoint(product.locationPoint);
    if (!point) return [];
    return [
      {
        id: product.id,
        point,
        title: readProductTitle(product.title, preferLanguage),
        thumbnail: resolveThumbnailUrl(product.thumbnail),
        category: product.contentCategory,
        subcategory: product.contentSubcategory,
      },
    ];
  });

// Chooses which points the map fits to. A selected region's boundary wins
// (recenter to the region); otherwise the product markers — but only in
// bounds mode (one-shot initial fit) or mirror mode (`fitToProducts`).
export const selectFitPoints = (opts: {
  regionBoundary?: ReadonlyArray<MapPoint> | null;
  markers: ReadonlyArray<ProductMarkerVm>;
  boundsMode: boolean;
  fitToProducts: boolean;
}): { regionPoints: MapPoint[]; productPoints: MapPoint[] } => ({
  regionPoints:
    opts.regionBoundary && opts.regionBoundary.length > 0
      ? [...opts.regionBoundary]
      : [],
  productPoints:
    opts.boundsMode || opts.fitToProducts
      ? opts.markers.map((m) => m.point)
      : [],
});
